import moment from 'moment';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';

import { useLoggedInUserRoles } from '~/components/Auth';
import { useApplicationConfig } from '~/data/applicationConfig';
import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import { useEmployee } from '~/data/employee';
import { EmpBasicData, EmpPosition } from '~/data/employee/types';
import { EmployeesIndexed, useAllEmployees } from '~/data/employees';
import { useAllEnums } from '~/data/enums';
import { AllEnums } from '~/data/enums/types';
import {
  LegalEntity,
  getCountryByLegalEntity,
  useAllLegalEntities,
} from '~/data/legalEntity';
import { useAllOrgUnits, useMyUnits } from '~/data/orgunits';
import { OrgTreeData, OrganizationUnit } from '~/data/orgunits/types';
import { useOrgTree } from '~/data/orgunits/useOrgTree';
import {
  EmploymentDetailsProposalProps,
  EmploymentPositionProposalProps,
} from '~/data/proposals';
import { UnitPosition } from '~/data/unitPositions';
import { useAllUnitPositions } from '~/data/unitPositions/useAllUnitPositions';
import {
  EmploymentProposalsDataAndConfig,
  filterAndSortEmploymentProposals,
} from '~/features/ScheduledEmploymentChanges/Common/EmploymentProposals';
import {
  AdditionalCostCentersState,
  Form,
} from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import {
  EditCustomField,
  jsEditCustomFieldData,
} from '~/legacy-components/CustomDataFields';
import { EmployeeProposalDecision } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { isRoleHR } from '~/services/Permissions';

import {
  getCustomCostCenterFieldAttributes,
  getManagerIds,
  mapEmpPositionProposalPutData,
  submitEmpPositionWorkflow,
} from './data';

export type CCCustomValue = {
  [key: string]: string | string[] | number | null | undefined;
};

export type CCCustomData = {
  [key: `${number}-cc-customData`]: CCCustomValue;
};

export type CCPercentFormValue = {
  [key: `${number}-cc-percent`]: string | undefined;
};

export type UPPercentFormValue = {
  [key: `${number}-percent`]: number | undefined;
};

export type ScheduleEmploymentFormValues = {
  fEmpLegalEntity: string;
  fInternalPositionPosition: string;
  fEmpJobTitle: string;
  fInternalPositionInfo: string;
  fInternalPositionStartDate: moment.Moment;
  fInternalPositionUnit: number;
  fEmpUnitPositions: number[];
  fEmpCostCenter: number;
  fInternalPositionManager: string;
  fInternalPositionProposalHrId?: number;
} & CCPercentFormValue &
  UPPercentFormValue &
  CCCustomData;

export type IndexedCustomFields = {
  [key: number]: EditCustomField[];
};

export type ScheduledEmploymentUnitOptions = {
  myUnits: OrganizationUnit[];
  otherUnits: OrganizationUnit[];
  allUnits: OrganizationUnit[];
  allUnitOptionsEnabled: boolean;
};

export type ScheduledEmploymentUnitSelection = {
  options: ScheduledEmploymentUnitOptions;
  onUnitChange: (newValue: number) => void;
};

export type UseScheduleEmploymentOutput = {
  legalEntities: LegalEntity[];
  unitSelection: ScheduledEmploymentUnitSelection;
  unitPositions: UnitPosition[];
  empData: EmpPosition;
  allEmployees: EmployeesIndexed;
  allEnums: AllEnums;
  empBasicData: EmpBasicData;
  managers: number[] | undefined;
  orgTree: OrgTreeData[];
  defaultData: EmpPosition;
  upcomingChangesOpen: boolean;
  proposals: EmploymentPositionProposalProps[];
  loading: boolean;
  activeDialog: string[];
  warningOpen: boolean;
  newEngine: boolean;
  isHRUser: boolean;
  customFields: IndexedCustomFields;

  onDialogChange: (activeDialogName: string) => void;
  getCostCenterValues: () => CostCenter[];
  onAdditionalCostCenterAdd: (id: number) => void;
  deleteSelectedCC: (id: number) => void;
  onUnitChange: (newValue: number) => void;
  onLegalEntityChange: (newValue: string) => void;
  setWarningOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUpComingChangesOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onProposalDecision: (
    proposal: EmploymentPositionProposalProps,
    decision?: EmployeeProposalDecision,
  ) => void;
};

type PropsType = {
  scheduleFormMethods: UseFormReturn<Form>;
  index: number;
  setAdditionalCostCenters: React.Dispatch<
    React.SetStateAction<AdditionalCostCentersState>
  >;
  additionalCostCenters: CostCenter[];
  empId: number;
  resetFormAfterProposalDecision: ({
    emplDetPropData,
    empPositionPropData,
  }: {
    emplDetPropData?: EmploymentDetailsProposalProps;
    empPositionPropData?: EmploymentPositionProposalProps;
  }) => void;
  empPositionPending?: EmpPosition;
  proposalsData: EmploymentProposalsDataAndConfig;
};

export const useScheduleEmployment = (
  props: PropsType,
): UseScheduleEmploymentOutput => {
  const {
    scheduleFormMethods,
    index,
    setAdditionalCostCenters,
    additionalCostCenters,
    empId,
    resetFormAfterProposalDecision,
    empPositionPending,
    proposalsData,
  } = props;

  // General Utils
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // Application configs: New Engine enabled
  const { applicationConfig } = useApplicationConfig();
  const unitForwardingEnabled =
    !!applicationConfig.fComConfig?.mgrConfig.fComEnableMgrEmployeeForwarding;

  // Logged user
  const myRoles = useLoggedInUserRoles();

  // Proposals data
  const {
    positions: { proposals: unsortedProposals, mutate: mutateProposals },
    config: { proposalsEnabled },
  } = proposalsData;
  const proposals = filterAndSortEmploymentProposals(
    unsortedProposals,
  ) as unknown as EmploymentPositionProposalProps[];
  const newEngine = proposalsEnabled;

  // Data: OrgTree & Units
  const { orgTree } = useOrgTree();

  const { orgUnits: allUnits } = useAllOrgUnits();
  const { myUnits } = useMyUnits(true);

  const unitOptions: ScheduledEmploymentUnitOptions = useMemo(() => {
    const myUnitIds = myUnits.map((u) => u.fOrgUnitId);
    const otherUnits = allUnits.filter(
      (unit) => !myUnitIds.includes(unit.fOrgUnitId),
    );
    const hasOtherUnits = otherUnits.length > 0;

    return {
      allUnits,
      myUnits,
      otherUnits,
      allUnitOptionsEnabled: unitForwardingEnabled && hasOtherUnits,
    };
  }, [allUnits, myUnits, unitForwardingEnabled]);

  // Form methods & states
  const { getValues, setValue, unregister, register } = scheduleFormMethods;
  const [upcomingChangesOpen, setUpComingChangesOpen] = useState(false);
  // Rename variable, used for Unit position & Cost Center dropdown opening
  const [activeDialog, setActiveDialog] = useState<string[]>([]);
  const [warningOpen, setWarningOpen] = useState(false);

  // Data: Managers
  const { data: managers, isLoading: managersIsLoading } = useSWR<number[]>(
    'managers',
    () => getManagerIds(),
    { revalidateOnFocus: false },
  );

  // Data: UnitPositions, LegalEntities, CostCenters
  const { unitPositions, isLoading: unitPositionsLoading } =
    useAllUnitPositions();
  const {
    legalEntities,
    isLoading: legalEntitiesLoading,
    hasData: legalEntityHasData,
  } = useAllLegalEntities();
  const {
    costCenters,
    isLoading: costCentersLoading,
    hasData: costCenterHasData,
  } = useAllCostCenters();
  const { allEmployees, isLoading: employeesLoading } = useAllEmployees();
  const { employee, isLoading: employeeLoading } = useEmployee();
  const { empBasicData, empPosition } = employee;
  const { allEnums, isLoading: enumsLoading } = useAllEnums();

  const latestEmpData = useMemo(
    () => (empPositionPending ? empPositionPending : empPosition),
    [empPosition, empPositionPending],
  );

  const onProposalDecision = async (
    proposal: EmploymentPositionProposalProps,
    decision?: EmployeeProposalDecision,
  ) => {
    const submitData = mapEmpPositionProposalPutData(
      proposal,
      myRoles,
      decision,
    );
    await submitEmpPositionWorkflow(
      empId,
      submitData,
      proposal.proposalId,
    ).then((r) => {
      if (r.error) {
        enqueueSnackbar(`${t(r.error)}`, {
          variant: 'error',
        });
      } else {
        resetFormAfterProposalDecision({
          empPositionPropData:
            //INFO: if it was a reject reset back to the last approved proposal
            proposals[decision === EmployeeProposalDecision.Reject ? 1 : 0],
        });
        mutateProposals();
      }
    });
  };

  const getFormPositionUnit = useCallback(
    (empData: EmpPosition) => {
      const empDataPU = empData ? empData.fInternalPositionUnit : -1;

      // @ts-ignore
      return getValues('empPosition.fInternalPositionUnit')
        ? getValues(`schedule.${index}.empPosition.fInternalPositionUnit`)
        : empDataPU;
    },
    [getValues, index],
  );

  // Cost center utils
  const getCostCenterValues = useCallback(
    (positionUnit?: number) => {
      const targetPU = positionUnit
        ? positionUnit
        : getFormPositionUnit(latestEmpData);

      const costCentersFiltered = costCenters.filter(
        (cc) =>
          cc.hasOwnProperty('fCostCenterUnits') &&
          cc.fCostCenterUnits.indexOf(targetPU) > -1,
      );
      return costCentersFiltered.length ? costCentersFiltered : costCenters;
    },
    [latestEmpData, getFormPositionUnit, costCenters],
  );

  const findCostCenterValue = useCallback(
    (costCenterId: number, unitId?: number) =>
      getCostCenterValues(unitId).find(
        (cc) => cc.fCostCenterId === costCenterId,
      ),
    [getCostCenterValues],
  );

  const onDialogChange = (activeDialogName: string) => {
    const costCenterValue = getValues(
      `schedule.${index}.empPosition.fEmpCostCenter`,
    );
    const unitPositions = getValues(
      `schedule.${index}.empPosition.fEmpUnitPositions`,
    );
    if (
      activeDialogName === 'costCenter' &&
      (!costCenterValue ||
        costCenterValue === -1 ||
        !findCostCenterValue(costCenterValue))
    ) {
      return;
    }
    if (
      activeDialogName === 'unitPosition' &&
      (!unitPositions || unitPositions.length === 0)
    ) {
      return;
    }
    if (activeDialog.includes(activeDialogName)) {
      setActiveDialog(
        activeDialog.filter((dialog) => dialog !== activeDialogName),
      );
    } else {
      setActiveDialog([...activeDialog, activeDialogName]);
    }
  };

  const onAdditionalCostCenterAdd = useCallback(
    (id: number) => {
      if (id > 0) {
        const selectedCCData = findCostCenterValue(id);
        if (selectedCCData) {
          setAdditionalCostCenters((activeState) => {
            const ccAlreadyAdded = (activeState?.[index] ?? []).find(
              (cc) => cc.fCostCenterId === selectedCCData.fCostCenterId,
            );
            return {
              ...activeState,
              ...(!ccAlreadyAdded
                ? { [index]: [...(activeState?.[index] || []), selectedCCData] }
                : {}),
            };
          });
          //INFO: if a new cost center has been added, it has to be registered and added a default value
          if (
            !getValues()?.schedule?.[index]?.empPosition?.[`${id}-cc-percent`]
          ) {
            register(`schedule.${index}.empPosition.${id}-cc-percent`);
            setValue(
              `schedule.${index}.empPosition.${id}-cc-percent`,
              '' as never,
              { shouldDirty: true },
            );
          }
        }
      }
    },
    [
      findCostCenterValue,
      getValues,
      index,
      register,
      setAdditionalCostCenters,
      setValue,
    ],
  );

  const deleteSelectedCC = (id: number) => {
    unregister(`schedule.${index}.empPosition.${id}-cc-percent`);
    setAdditionalCostCenters((activeState) => {
      return {
        ...activeState,
        ...{
          [index]: (activeState?.[index] || []).filter(
            (cc) => cc.fCostCenterId !== id,
          ),
        },
      };
    });
  };

  // Unit selection
  const onUnitChange = (newValue: number) => {
    setValue(`schedule.${index}.empPosition.fEmpUnitPositions`, []);
    if (newValue === -1 || !managers) {
      setValue(`schedule.${index}.empPosition.fEmpCostCenter`, -1);
    } else {
      const orgUnit = (unitForwardingEnabled ? allUnits : myUnits).find(
        (unit) => unit.fOrgUnitId === newValue,
      );
      const selectedManager: string = orgUnit?.fOrgUnitManager.toString() ?? '';

      setValue(
        `schedule.${index}.empPosition.fInternalPositionManager`,
        selectedManager,
      );
      const empPositionValues = getValues(`schedule.${index}.empPosition`);
      if (empPositionValues) {
        const defaultCCLinked = findCostCenterValue(
          empPositionValues.fEmpCostCenter,
          newValue,
        );
        if (!defaultCCLinked) {
          setValue(`schedule.${index}.empPosition.fEmpCostCenter`, -1);
          const additionalCCKeysIndex = Object.keys(empPositionValues)
            .filter((key) => key.includes('-cc'))
            .map((key) => parseInt(key.split('-cc')[0]));
          additionalCCKeysIndex.forEach((ccIndex) => {
            if (
              !findCostCenterValue(ccIndex, newValue) ||
              ccIndex === newValue
            ) {
              deleteSelectedCC(ccIndex);
            }
          });
        }
      }
    }
  };

  const unitSelection: ScheduledEmploymentUnitSelection = {
    options: unitOptions,
    onUnitChange,
  };

  // Legal entity selection
  const onLegalEntityChange = (newValue: string) => {
    if (newValue === '') {
      setValue(`schedule.${index}.empPosition.fInternalPositionPosition`, '');
    }
  };

  useEffect(() => {
    if (latestEmpData) {
      //INFO: the default value sets the form values for the additional CC, but they have to be added to the AdditionalCostCenters object
      // which will render the fields
      latestEmpData.fEmpCostCenters.forEach((cc) => {
        if (!cc.fEmpCostCenterIsDefault) {
          onAdditionalCostCenterAdd(cc.fEmpCostCenter);
        }
      });
    }
  }, [latestEmpData, setValue, onAdditionalCostCenterAdd, index, getValues]);

  //CUSTOM DATA
  const {
    data: customCostCenterFieldAttributes,
    isLoading: fieldAttributesLoading,
  } = useSWR(
    'customCostCentersFieldsFieldAttributes',
    () => getCustomCostCenterFieldAttributes(),
    { revalidateOnFocus: false, refreshInterval: 0 },
  );

  const customDataLoading = fieldAttributesLoading;

  const legalEntityId =
    ['NEW', 'PENDING'].includes(empBasicData.fEmpStatus) && empPositionPending
      ? empPositionPending?.fEmpLegalEntity
      : empPosition?.fEmpLegalEntity;

  const isLoading =
    customDataLoading || (legalEntitiesLoading && !legalEntityHasData);

  const customFields = useMemo(() => {
    if (!isLoading) {
      let hashedFields = Object.assign(
        {},
        ...customCostCenterFieldAttributes!.map((field) => ({
          [field.fCustomDataAttributeName]: field,
        })),
      );

      const legalEntityCountryForUser = getCountryByLegalEntity(
        legalEntityId,
        legalEntities,
      );

      const additionalCCCustomFields = additionalCostCenters.reduce(
        (prevValue, curValue) => ({
          ...prevValue,
          [curValue.fCostCenterId]: jsEditCustomFieldData({
            strEntity: 'COST_CENTER',
            customFieldAttributesHash: hashedFields,
            legalEntityCountry: legalEntityCountryForUser,
            customNamePrefix: `schedule.${index}.empPosition.${curValue.fCostCenterId}-cc-customData.`,
            allEnums,
          }),
        }),
        {},
      );
      const defaultCC = latestEmpData.fEmpCostCenters.find(
        (cc) => cc.fEmpCostCenterIsDefault,
      );
      return {
        ...additionalCCCustomFields,
        ...(defaultCC && {
          [defaultCC.fEmpCostCenter]: jsEditCustomFieldData({
            strEntity: 'COST_CENTER',
            customFieldAttributesHash: hashedFields,
            legalEntityCountry: legalEntityCountryForUser,
            customNamePrefix: `schedule.${index}.empPosition.${defaultCC.fEmpCostCenter}-cc-customData.`,
            allEnums,
          }),
        }),
      };
    }
    return [];
  }, [
    additionalCostCenters,
    allEnums,
    customCostCenterFieldAttributes,
    index,
    isLoading,
    latestEmpData.fEmpCostCenters,
    legalEntities,
    legalEntityId,
  ]);

  return {
    legalEntities,
    loading:
      unitPositionsLoading ||
      (legalEntitiesLoading && !legalEntityHasData) ||
      (costCentersLoading && !costCenterHasData) ||
      managersIsLoading ||
      employeesLoading ||
      employeeLoading ||
      enumsLoading,
    unitSelection,
    onDialogChange,
    activeDialog,
    unitPositions,
    getCostCenterValues,
    onAdditionalCostCenterAdd,
    deleteSelectedCC,
    onUnitChange,
    empBasicData,
    empData: empPositionPending ? empPositionPending : empPosition,
    defaultData: empPosition,
    managers,
    allEmployees,
    allEnums,
    onLegalEntityChange,
    warningOpen,
    setWarningOpen,
    orgTree,
    upcomingChangesOpen,
    setUpComingChangesOpen,
    proposals,
    onProposalDecision,
    customFields,
    isHRUser: isRoleHR(myRoles),
    newEngine,
  };
};
